import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { History, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const fmt = (ts) => new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const initials = (n) => (n || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
const dayKey = (ts) => new Date(ts).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

export function ActivityTab({ customerId }) {
  const [data, setData] = useState(null);
  const [actor, setActor] = useState("");
  const load = useCallback(() => api.get(`/company/${customerId}/activity`, { params: actor ? { actor } : {} }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))), [customerId, actor]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-slate-500">Loading activity…</div>;
  const days = data.items.reduce((acc, it) => { const k = dayKey(it.at); (acc[k] = acc[k] || []).push(it); return acc; }, {});
  return (
    <div className="space-y-4" data-testid="activity-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><History className="h-5 w-5" />Activity</h3>
          <p className="text-sm text-slate-600">Who changed what — driver edits, uploads, requests and more — across everyone with access to this company.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="activity-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
      </div>
      {data.actors.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="activity-actor-filter">
          <button type="button" onClick={() => setActor("")} className={`rounded-full border px-3 py-1 text-xs font-medium ${!actor ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`} data-testid="activity-actor-all">Everyone</button>
          {data.actors.map((a) => (
            <button key={a.login_id} type="button" onClick={() => setActor(a.login_id)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${actor === a.login_id ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`} data-testid={`activity-actor-${a.login_id}`}>
              <span className={`h-4 w-4 rounded-full text-[9px] font-bold inline-flex items-center justify-center ${a.role === "admin" ? "bg-slate-700 text-white" : "bg-emerald-600 text-white"}`}>{initials(a.name)}</span>{a.name}<span className="opacity-60">· {a.count}</span>
            </button>
          ))}
        </div>
      )}
      {data.items.length === 0 && <p className="text-sm text-slate-500" data-testid="activity-empty">No activity recorded yet. Changes made from now on will appear here.</p>}
      {Object.entries(days).map(([day, items]) => (
        <div key={day}>
          <div className="tag-mono text-slate-500 mb-2">{day.toUpperCase()}</div>
          <ol className="relative border-l-2 border-slate-200 ml-3 space-y-3">
            {items.map((it) => (
              <li key={it.id} className="ml-4 relative" data-testid={`activity-item-${it.id}`}>
                <span className={`absolute -left-[25px] top-0.5 h-5 w-5 rounded-full text-[9px] font-bold inline-flex items-center justify-center ring-2 ring-white ${it.actor.role === "admin" ? "bg-slate-700 text-white" : "bg-emerald-600 text-white"}`} title={it.actor.name}>{initials(it.actor.name)}</span>
                <div className="text-sm text-slate-900"><span className="font-semibold">{it.actor.name}</span>{it.actor.role === "admin" && <span className="text-[10px] ml-1 rounded bg-slate-100 px-1 py-0.5 text-slate-600">Safer Highways</span>} <span className="text-slate-700">{it.action.toLowerCase()}</span>{it.target && <span className="font-medium"> — {it.target}</span>}</div>
                <div className="text-xs text-slate-500">{fmt(it.at)}</div>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}
